<template>
  <v-container
    fluid
    class="px-0 py-0"
    style="background: #f5f7fb; min-height: 100vh; padding: 0px !important"
  >
    <!-- Page Loading Overlay -->

    <v-overlay :value="pageLoading" z-index="999" opacity="0.75">
      <v-card class="p-8 rounded-xl text-center" elevation="8" width="280">
        <v-avatar color="#EEF4FF" size="72" class="mb-4">
          <v-progress-circular
            indeterminate
            size="42"
            width="4"
            color="#2563EB"
          />
        </v-avatar>

        <h4 class="font-weight-bold">Loading...</h4>

        <div style="font-size: 12px" class="text-body-1 grey--text mt-2">
          Please wait while we process your request.
        </div>
      </v-card>
    </v-overlay>

    <!-- Token Error Full Screen -->
    <v-container
      v-if="!tokenError"
      fluid
      class="d-flex align-items-center justify-content-center"
      style="min-height: 100vh"
    >
      <v-row class="w-100 align-items-center justify-content-center">
        <v-col cols="12" sm="8" md="5" lg="4">
          <v-card rounded="xl" elevation="4" class="p-8 text-center">
            <v-avatar color="#FEF2F2" size="100" class="mb-5">
              <v-avatar color="#DC2626" size="72">
                <v-icon size="40" color="#ffffff">
                  mdi-shield-alert-outline
                </v-icon>
              </v-avatar>
            </v-avatar>

            <h2 class="text-h6 font-weight-bold grey--text text--darken-3 mb-4">
              Access Denied
            </h2>

            <div
              style="font-size: 14px"
              class="text-body-1 grey--text text--darken-1 mb-6"
            >
              This survey link is invalid or has expired. Please request a new
              link from your service contact.
            </div>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- Main Survey -->
    <v-container v-else fluid class="px-0 py-0" style="padding: 0px !important">
      <!-- ── Header ── -->
      <v-card
        flat
        elevation="0"
        color="transparent"
        class="mb- px-4 border"
        style="position: sticky; top: 0px; z-index: 50"
      >
        <v-row align="center" no-gutters>
          <v-col cols="12" sm="9" class="my-auto d-flex align-items-center">
             <v-avatar color="#EEF4FF" size="50" class="mr-4">
              <v-icon size="30" color="#2563EB">
                mdi-clipboard-check-outline
              </v-icon>
            </v-avatar>
            <div>
            <h1
              class="text-h5 text-sm-h4 font-weight-bold blue-grey--text text--darken-3 mb-1"
            >
              Customer Feedback Portal
            </h1>
            <p class="text-body-2 grey--text text--darken-1 mb-0">
              Thank you for taking a few minutes to share your feedback. Your
              responses help us improve our products and services.
            </p>
            </div>
          </v-col>
          <v-col cols="12" sm="3" class="d-none d-md-flex justify-content-end my-auto">
            <v-chip
              small
              pill
              color="#EFF6FF"
              text-color="#2563EB"
              class="font-weight-bold px-2"
            >
              <v-icon left x-small color="#2563EB">
                mdi-shield-check-outline
              </v-icon>

              Secure Survey
            </v-chip>
          </v-col>
        </v-row>
      </v-card>
      <div class="px-6" style="max-width: 1400px; margin: 0 auto">
        <!-- ── Info Alert ── -->
        <v-alert
          elevation="1"
          color="#FFFFFF"
          class="my-5 rounded-xl"
          dismissible
          style="border-left: 4px solid #2563eb"
        >
          <template v-slot:prepend>
            <v-avatar color="#DBEAFE" size="40" class="mr-3">
              <v-icon color="#2563EB" size="22">
                mdi-information-outline
              </v-icon>
            </v-avatar>
          </template>

          <div>
            <div
              class="subtitle-2 font-weight-bold mb-1"
              style="color: #1e3a8a"
            >
              Survey Information
            </div>

            <div class="text-body-2" style="color: #64748b">
              Please complete all required questions before submitting the
              survey. Your feedback helps us improve our products and services.
            </div>
          </div>
        </v-alert>

        <v-form ref="reviewForm">
          <!-- ── ROW 1: Overall Experience + Service Quality ── -->
          <v-row>
            <!-- Overall Experience -->
            <v-col cols="12" md="6" class="pb-4">
              <v-card rounded="lg" elevation="1" class="p-5 fill-height">
                <div class="d-flex align-items-center mb-4">
                  <v-avatar color="#EEF4FF" size="40" class="mr-3">
                    <v-icon color="#2563EB" size="22">
                      mdi-star-outline
                    </v-icon>
                  </v-avatar>
                  <h3
                    class="text-subtitle-1 font-weight-bold blue-grey--text text--darken-3 mb-0"
                  >
                    1. Overall Experience
                  </h3>
                </div>
                <v-divider class="mb-4" />

                <p
                  class="text-body-1 font-weight-bold grey--text text--darken-3 mb-2"
                >
                  1. How satisfied are you with your overall experience?
                </p>
                <v-radio-group
                  v-model="survey.overall_satisfaction"
                  hide-details
                  class="mt-0 mb-4"
                >
                  <v-radio value="Very satisfied" color="#16A34A" class="mb-3">
                    <template #label>
                      <span class="font-weight-bold" style="color: #16a34a">
                        Very satisfied
                      </span>
                    </template>
                  </v-radio>

                  <v-radio value="Satisfied" color="#22C55E" class="mb-3">
                    <template #label>
                      <span class="font-weight-bold" style="color: #22c55e">
                        Satisfied
                      </span>
                    </template>
                  </v-radio>

                  <v-radio value="Neutral" color="#F59E0B" class="mb-3">
                    <template #label>
                      <span class="font-weight-bold" style="color: #f59e0b">
                        Neutral
                      </span>
                    </template>
                  </v-radio>

                  <v-radio value="Dissatisfied" color="#F97316" class="mb-3">
                    <template #label>
                      <span class="font-weight-bold" style="color: #f97316">
                        Dissatisfied
                      </span>
                    </template>
                  </v-radio>

                  <v-radio value="Very dissatisfied" color="#DC2626">
                    <template #label>
                      <span class="font-weight-bold" style="color: #dc2626">
                        Very dissatisfied
                      </span>
                    </template>
                  </v-radio>
                </v-radio-group>

                <v-divider class="mb-4" />

                <p
                  class="text-body-1 font-weight-bold grey--text text--darken-3 mb-2"
                >
                  2. How well did our product/service meet your expectations?
                </p>
                <v-radio-group
                  v-model="survey.expectations"
                  hide-details
                  class="mt-0"
                >
                  <v-radio
                    value="Exceeded expectations"
                    color="#16A34A"
                    class="mb-3"
                  >
                    <template #label>
                      <div class="d-flex align-items-center">
                        <span class="font-weight-bold" style="color: #16a34a">
                          Exceeded expectations
                        </span>
                      </div>
                    </template>
                  </v-radio>

                  <v-radio
                    value="Met expectations"
                    color="#2563EB"
                    class="mb-3"
                  >
                    <template #label>
                      <div class="d-flex align-items-center">
                        <span class="font-weight-bold" style="color: #2563eb">
                          Met expectations
                        </span>
                      </div>
                    </template>
                  </v-radio>

                  <v-radio value="Slightly below" color="#F59E0B" class="mb-3">
                    <template #label>
                      <div class="d-flex align-items-center">
                        <span class="font-weight-bold" style="color: #f59e0b">
                          Slightly below
                        </span>
                      </div>
                    </template>
                  </v-radio>

                  <v-radio value="Far below" color="#DC2626">
                    <template #label>
                      <div class="d-flex align-items-center">
                        <span class="font-weight-bold" style="color: #dc2626">
                          Far below
                        </span>
                      </div>
                    </template>
                  </v-radio>
                </v-radio-group>
              </v-card>
            </v-col>

            <!-- Service Quality -->
            <v-col cols="12" md="6" class="pb-4">
              <v-card rounded="lg" elevation="1" class="p-5 fill-height">
                <div class="d-flex align-items-center mb-4">
                  <v-avatar color="#D1FAE5" size="40" class="mr-3">
                    <v-icon color="#059669" size="22">
                      mdi-medal-outline
                    </v-icon>
                  </v-avatar>
                  <h3
                    class="text-subtitle-1 font-weight-bold blue-grey--text text--darken-3 mb-0"
                  >
                    2. Service Quality
                  </h3>
                </div>
                <v-divider class="mb-4" />

                <p
                  class="text-body-1 font-weight-bold grey--text text--darken-3 mb-2"
                >
                  3. How would you rate the quality of our service?
                </p>
                <v-radio-group
                  v-model="survey.service_quality"
                  hide-details
                  class="mt-0 mb-4"
                >
                  <v-radio value="Excellent" color="#16A34A" class="mb-3">
                    <template #label>
                      <div class="d-flex align-items-center">
                        <span class="font-weight-bold" style="color: #16a34a">
                          Excellent
                        </span>
                      </div>
                    </template>
                  </v-radio>

                  <v-radio value="Good" color="#22C55E" class="mb-3">
                    <template #label>
                      <div class="d-flex align-items-center">
                        <span class="font-weight-bold" style="color: #22c55e">
                          Good
                        </span>
                      </div>
                    </template>
                  </v-radio>

                  <v-radio value="Fair" color="#F59E0B" class="mb-3">
                    <template #label>
                      <div class="d-flex align-items-center">
                        <span class="font-weight-bold" style="color: #f59e0b">
                          Fair
                        </span>
                      </div>
                    </template>
                  </v-radio>

                  <v-radio value="Poor" color="#F97316" class="mb-3">
                    <template #label>
                      <div class="d-flex align-items-center">
                        <span class="font-weight-bold" style="color: #f97316">
                          Poor
                        </span>
                      </div>
                    </template>
                  </v-radio>

                  <v-radio value="Very Poor" color="#DC2626">
                    <template #label>
                      <div class="d-flex align-items-center">
                        <span class="font-weight-bold" style="color: #dc2626">
                          Very Poor
                        </span>
                      </div>
                    </template>
                  </v-radio>
                </v-radio-group>

                <v-divider class="mb-4" />

                <p
                  class="text-body-1 font-weight-bold grey--text text--darken-3 mb-2"
                >
                  4. How satisfied are you with the value for money?
                </p>
                <v-radio-group
                  v-model="survey.value_for_money"
                  hide-details
                  class="mt-0"
                >
                  <v-radio color="#16A34A" value="Very satisfied" class="mb-2">
                    <template #label>
                      <span class="font-weight-medium" style="color: #16a34a">
                        Very satisfied
                      </span>
                    </template>
                  </v-radio>

                  <v-radio color="#22C55E" value="Satisfied" class="mb-2">
                    <template #label>
                      <span class="font-weight-medium" style="color: #22c55e">
                        Satisfied
                      </span>
                    </template>
                  </v-radio>

                  <v-radio color="#F59E0B" value="Neutral" class="mb-2">
                    <template #label>
                      <span class="font-weight-medium" style="color: #f59e0b">
                        Neutral
                      </span>
                    </template>
                  </v-radio>

                  <v-radio color="#F97316" value="Dissatisfied" class="mb-2">
                    <template #label>
                      <span class="font-weight-medium" style="color: #f97316">
                        Dissatisfied
                      </span>
                    </template>
                  </v-radio>

                  <v-radio color="#DC2626" value="Very dissatisfied">
                    <template #label>
                      <span class="font-weight-medium" style="color: #dc2626">
                        Very dissatisfied
                      </span>
                    </template>
                  </v-radio>
                </v-radio-group>
              </v-card>
            </v-col>
          </v-row>

          <!-- ── ROW 2: Customer Support + Loyalty ── -->
          <v-row class="mb-2">
            <!-- Customer Support -->
            <v-col cols="12" md="6" class="pb-4">
              <v-card rounded="lg" elevation="1" class="p-5 fill-height">
                <div class="d-flex align-items-center mb-4">
                  <v-avatar color="#F3E8FF" size="40" class="mr-3">
                    <v-icon color="#7C3AED" size="22"> mdi-headset </v-icon>
                  </v-avatar>
                  <h3
                    class="text-subtitle-1 font-weight-bold blue-grey--text text--darken-3 mb-0"
                  >
                    3. Customer Support
                    <span class="text-caption grey--text font-weight-regular"
                      >(if applicable)</span
                    >
                  </h3>
                </div>
                <v-divider class="mb-4" />

                <p
                  class="text-body-1 font-weight-bold grey--text text--darken-3 mb-2"
                >
                  5. How satisfied are you with our customer support?
                </p>
                <v-radio-group
                  v-model="survey.customer_support"
                  hide-details
                  class="mt-0 mb-4"
                >
                  <v-radio value="Very satisfied" color="#16A34A" class="mb-3">
                    <template #label>
                      <div class="d-flex align-items-center">
                        <span class="font-weight-bold" style="color: #16a34a">
                          Very satisfied
                        </span>
                      </div>
                    </template>
                  </v-radio>

                  <v-radio value="Satisfied" color="#22C55E" class="mb-3">
                    <template #label>
                      <div class="d-flex align-items-center">
                        <v-icon small color="#22C55E" class="mr-2">
                          mdi-emoticon-happy-outline
                        </v-icon>

                        <span class="font-weight-bold" style="color: #22c55e">
                          Satisfied
                        </span>
                      </div>
                    </template>
                  </v-radio>

                  <v-radio value="Neutral" color="#F59E0B" class="mb-3">
                    <template #label>
                      <div class="d-flex align-items-center">
                        <v-icon small color="#F59E0B" class="mr-2">
                          mdi-emoticon-neutral-outline
                        </v-icon>

                        <span class="font-weight-bold" style="color: #f59e0b">
                          Neutral
                        </span>
                      </div>
                    </template>
                  </v-radio>

                  <v-radio value="Dissatisfied" color="#F97316" class="mb-3">
                    <template #label>
                      <div class="d-flex align-items-center">
                        <span class="font-weight-bold" style="color: #f97316">
                          Dissatisfied
                        </span>
                      </div>
                    </template>
                  </v-radio>

                  <v-radio
                    value="Very dissatisfied"
                    color="#DC2626"
                    class="mb-3"
                  >
                    <template #label>
                      <div class="d-flex align-items-center">
                        <span class="font-weight-bold" style="color: #dc2626">
                          Very dissatisfied
                        </span>
                      </div>
                    </template>
                  </v-radio>

                  <v-radio value="Not applicable" color="#64748B">
                    <template #label>
                      <div class="d-flex align-items-center">
                        <span class="font-weight-bold" style="color: #64748b">
                          Not applicable
                        </span>
                      </div>
                    </template>
                  </v-radio>
                </v-radio-group>

                <v-divider class="mb-4" />

                <p
                  class="text-body-1 font-weight-bold grey--text text--darken-3 mb-2"
                >
                  6. Was your issue or request resolved in a timely manner?
                </p>
                <v-radio-group
                  v-model="survey.issue_resolved"
                  hide-details
                  class="mt-0"
                >
                  <v-radio color="#16A34A" value="Yes" class="mb-2">
                    <template #label>
                      <span class="font-weight-medium" style="color: #16a34a">
                        Yes
                      </span>
                    </template>
                  </v-radio>

                  <v-radio color="#F59E0B" value="Partially" class="mb-2">
                    <template #label>
                      <span class="font-weight-medium" style="color: #f59e0b">
                        Partially
                      </span>
                    </template>
                  </v-radio>

                  <v-radio color="#DC2626" value="No" class="mb-2">
                    <template #label>
                      <span class="font-weight-medium" style="color: #dc2626">
                        No
                      </span>
                    </template>
                  </v-radio>

                  <v-radio color="#64748B" value="Not applicable">
                    <template #label>
                      <span class="font-weight-medium" style="color: #64748b">
                        Not applicable
                      </span>
                    </template>
                  </v-radio>
                </v-radio-group>
              </v-card>
            </v-col>

            <!-- Loyalty & Recommendation -->
            <v-col cols="12" md="6" class="pb-4">
              <v-card rounded="lg" elevation="1" class="p-5 fill-height">
                <div class="d-flex align-items-center mb-4">
                  <v-avatar color="#FFF1E6" size="40" class="mr-3">
                    <v-icon color="#F97316" size="22">
                      mdi-account-group-outline
                    </v-icon>
                  </v-avatar>
                  <h3
                    class="text-subtitle-1 font-weight-bold blue-grey--text text--darken-3 mb-0"
                  >
                    4. Loyalty &amp; Recommendation
                  </h3>
                </div>
                <v-divider class="mb-4" />

                <p
                  class="text-body-1 font-weight-bold grey--text text--darken-3 mb-3"
                >
                  7. How likely are you to recommend us to others?
                </p>

                <!-- NPS Scale -->
                <v-radio-group
                  v-if="false"
                  v-model="survey.loyalty_recomm"
                  row
                  hide-details
                  class="mt-0 mb-2 flex-wrap"
                >
                  <v-radio
                    v-for="n in [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]"
                    :key="n"
                    :label="String(n)"
                    :value="n"
                    color="orange darken-2"
                    class="mr-1 mb-1"
                  />
                </v-radio-group>
                <v-radio-group
                  v-model="survey.loyalty_recomm"
                  row
                  hide-details
                  class="mt-0 mb-3"
                >
                  <v-radio
                    v-for="n in 11"
                    :key="n - 1"
                    :value="n - 1"
                    class="mr-1 mb-2"
                    :color="n <= 6 ? '#DC2626' : n <= 8 ? '#F59E0B' : '#16A34A'"
                  >
                    <template #label>
                      <span
                        :style="{
                          color:
                            n <= 6 ? '#DC2626' : n <= 8 ? '#F59E0B' : '#16A34A',
                          fontWeight: 600,
                        }"
                      >
                        {{ n - 1 }}
                      </span>
                    </template>
                  </v-radio>
                </v-radio-group>

                <div
                  class="d-flex justify-space-between align-items-center mb-4"
                >
                  <v-chip
                    small
                    color="#FEF2F2"
                    text-color="#DC2626"
                    class="font-weight-medium px-2 mr-4"
                  >
                    <v-icon left size="14"> mdi-emoticon-sad-outline </v-icon>
                    0 = Not likely
                  </v-chip>

                  <v-chip
                    small
                    color="#ECFDF5"
                    text-color="#16A34A"
                    class="font-weight-medium px-2"
                  >
                    <v-icon left size="14"> mdi-emoticon-happy-outline </v-icon>
                    10 = Extremely likely
                  </v-chip>
                </div>

                <v-divider class="mb-4" />

                <p
                  class="text-body-1 font-weight-bold grey--text text--darken-3 mb-2"
                >
                  8. How likely are you to use our product/service again?
                </p>
                <v-radio-group
                  v-model="survey.use_again"
                  hide-details
                  class="mt-0"
                >
                  <v-radio color="#16A34A" value="Very likely" class="mb-2">
                    <template #label>
                      <span class="font-weight-medium" style="color: #16a34a">
                        Very likely
                      </span>
                    </template>
                  </v-radio>

                  <v-radio color="#22C55E" value="Likely" class="mb-2">
                    <template #label>
                      <span class="font-weight-medium" style="color: #22c55e">
                        Likely
                      </span>
                    </template>
                  </v-radio>

                  <v-radio color="#F59E0B" value="Neutral" class="mb-2">
                    <template #label>
                      <span class="font-weight-medium" style="color: #f59e0b">
                        Neutral
                      </span>
                    </template>
                  </v-radio>

                  <v-radio color="#F97316" value="Unlikely" class="mb-2">
                    <template #label>
                      <span class="font-weight-medium" style="color: #f97316">
                        Unlikely
                      </span>
                    </template>
                  </v-radio>

                  <v-radio color="#DC2626" value="Very unlikely">
                    <template #label>
                      <span class="font-weight-medium" style="color: #dc2626">
                        Very unlikely
                      </span>
                    </template>
                  </v-radio>
                </v-radio-group>
              </v-card>
            </v-col>
          </v-row>

          <!-- ── ROW 3: Feedback (3 textareas) ── -->
          <v-card rounded="lg" elevation="1" class="p-5 mb-6">
            <div class="d-flex align-items-center mb-4">
              <v-avatar color="#dbf5ef" size="40" class="mr-3">
                <v-icon color="#0F766E" size="22">
                  mdi-message-text-outline
                </v-icon>
              </v-avatar>
              <h3
                class="text-subtitle-1 font-weight-bold blue-grey--text text--darken-3 mb-0"
              >
                5. Feedback
              </h3>
            </div>
            <v-divider class="mb-5" />

            <v-row>
              <v-col cols="12" md="4" class="pb-2 pb-md-0">
                <p
                  class="text-body-2 font-weight-bold grey--text text--darken-3 mb-2"
                >
                  9. What did you like most about your experience?
                </p>
                <v-textarea
                  v-model="survey.liked_most"
                  rows="2"
                  flat
                  solo
                  style="box-shadow: none"
                  color="transparent"
                />
              </v-col>
              <v-col cols="12" md="4" class="pb-2 pb-md-0">
                <p
                  class="text-body-2 font-weight-bold grey--text text--darken-3 mb-2"
                >
                  10. What can we improve?
                </p>
                <v-textarea
                  v-model="survey.improvement"
                  rows="2"
                  flat
                  solo
                  style="box-shadow: none"
                  color="transparent"
                />
              </v-col>
              <v-col cols="12" md="4">
                <p
                  class="text-body-2 font-weight-bold grey--text text--darken-3 mb-2"
                >
                  11. Additional comments or suggestions
                </p>
                <v-textarea
                  v-model="survey.comment"
                  rows="2"
                  flat
                  solo
                  style="box-shadow: none"
                  color="transparent"
                  counter="250"
                  maxlength="250"
                />
              </v-col>
            </v-row>
          </v-card>
        </v-form>
        <v-card rounded="lg" elevation="2" class="p-5 mb-6 fill-height">
          <div class="d-flex align-start mb-4">
            <v-avatar color="#EEF4FF" size="52" class="mr-4">
              <v-icon color="#2563EB" size="26"> mdi-heart-outline </v-icon>
            </v-avatar>

            <div>
              <h4
                class="text-subtitle-1 font-weight-bold blue-grey--text text--darken-3 mb-2"
              >
                Thank You for Your Time
              </h4>

              <div class="text-body-2 grey--text text--darken-1">
                Thank you for taking the time to participate in this survey.
                Your valuable feedback helps us improve our products and deliver
                better service experiences.
              </div>
            </div>
          </div>
          <v-row dense>
            <!-- All Responses -->
            <v-col cols="12" sm="6" md="4">
              <v-card flat rounded="lg" color="#F0FDF4" class="p-3 h-100">
                <div class="d-flex align-center">
                  <v-avatar color="#DCFCE7" size="42" class="mr-3">
                    <v-icon color="#16A34A" size="18"> mdi-check </v-icon>
                  </v-avatar>

                  <div>
                    <div
                      class="text-caption font-weight-bold"
                      style="color: #15803d"
                    >
                      All Responses
                    </div>

                    <div class="text-caption grey--text text--darken-1">
                      Anonymous
                    </div>
                  </div>
                </div>
              </v-card>
            </v-col>

            <!-- Data Safe -->
            <v-col cols="12" sm="6" md="4">
              <v-card flat rounded="lg" color="#EFF6FF" class="p-3 h-100">
                <div class="d-flex align-center">
                  <v-avatar color="#DBEAFE" size="42" class="mr-3">
                    <v-icon color="#2563EB" size="18">
                      mdi-shield-check-outline
                    </v-icon>
                  </v-avatar>

                  <div>
                    <div
                      class="text-caption font-weight-bold"
                      style="color: #1d4ed8"
                    >
                      Your Data is Safe
                    </div>

                    <div class="text-caption grey--text text--darken-1">
                      Secure &amp; Confidential
                    </div>
                  </div>
                </div>
              </v-card>
            </v-col>

            <!-- Required Questions -->
            <v-col cols="12" sm="6" md="4">
              <v-card flat rounded="lg" color="#FAF5FF" class="p-3 h-100">
                <div class="d-flex align-center">
                  <v-avatar color="#E9D5FF" size="42" class="mr-3">
                    <v-icon color="#7C3AED" size="18">
                      mdi-lock-outline
                    </v-icon>
                  </v-avatar>

                  <div>
                    <div
                      class="text-caption font-weight-bold"
                      style="color: #6d28d9"
                    >
                      Required Questions
                    </div>

                    <div class="text-caption grey--text text--darken-1">
                      Please complete all
                    </div>
                  </div>
                </div>
              </v-card>
            </v-col>
          </v-row>
        </v-card>
      </div>

      <!-- ── Sticky Footer Bar ── -->
      <v-card
        flat
        class="px-4 py-2 border"
        style="position: sticky; bottom: 0px"
      >
        <v-row align="center" no-gutters>
          <!-- Status indicators -->
          <v-col cols="12" sm="7" class="my-auto">
            <div class="d-flex align-items-center">
              <div class="text-body-1 text--secondary font-weight-bold mr-4">
                Powered by
              </div>
              <v-card outlined class="powered-card px-4 py-2" elevation="0">
                <div class="d-flex align-items-center">
                  <v-avatar size="42" class="mr-3 white">
                    <img
                      src="https://bthrust-fsm.s3.ap-southeast-1.amazonaws.com/SL012021VF/files/image/png/ByNfUIAmQmR6c8SboWJj.png"
                      alt="Genic Teams"
                      width="28"
                    />
                  </v-avatar>

                  <div>
                    <h6
                      class="font-weight-bold text-subtitle-1 text-grey-darken-4 mb-0"
                    >
                      Genic Teams
                    </h6>

                    <div class="caption text--secondary mb-0">
                      Smart Field Service Platform
                    </div>
                  </div>
                </div>
              </v-card>
            </div>
          </v-col>

          <!-- Action buttons -->
          <v-col cols="12" sm="5" class="d-flex justify-content-end my-auto">
            <v-btn
              outlined
              color="#64748B"
              class="mr-3 text-capitalize font-weight-medium px-6"
              rounded
              style="font-size: 14px"
              large
              elevation="0"
              @click="resetForm"
            >
              <v-icon left size="18">mdi-close</v-icon>
              Cancel
            </v-btn>
            <v-btn
              color="#2563eb"
              depressed
              :disabled="!isFormValid"
              :loading="submitting"
              @click="submitReview"
              class="text-capitalize font-weight-bold white--text px-8"
              style="color: #ffffff; font-size: 14px"
              rounded
              large
              elevation="0"
            >
              <v-icon colo="#ffffff" left small>mdi-send</v-icon>
              Submit Survey
            </v-btn>
          </v-col>
        </v-row>
      </v-card>
    </v-container>

    <!-- ══════════════════════════════════════════
         SUCCESS / ERROR / WARNING DIALOG
    ══════════════════════════════════════════ -->
    <v-dialog v-model="responseDialog" persistent :attach="dialogAttach">
      <!-- SUCCESS -->
      <v-card
        rounded="xl"
        class="p-2 text-center"
        v-if="dialogState.type === 'success'"
      >
        <v-card-text class="pt-10 pb-4 px-8">
          <!-- Decorative icon with ring -->
          <div class="d-flex justify-content-center mb-6">
            <v-avatar color="#DCFCE7" size="110">
              <v-avatar color="#16A34A" size="80">
                <v-icon size="44" color="#ffffff"> mdi-check </v-icon>
              </v-avatar>
            </v-avatar>
          </div>

          <h2
            class="text-h5 font-weight-bold blue-grey--text text--darken-3 mb-2"
          >
            Success!
          </h2>
          <v-divider
            class="mx-auto mb-4"
            style="max-width: 48px; border-color: #4caf50; border-width: 2px"
          />
          <p
            class="text-body-1 grey--text text--darken-1 mb-6"
            style="font-size: 14px"
          >
            Thank you for completing the Customer Satisfaction Survey.<br />
            Your feedback has been submitted successfully.
          </p>

          <!-- Appreciation box -->
          <v-card flat rounded="xl" color="#ECFDF5" class="p-5 mb-3">
            <div class="d-flex align-items-start text-left">
              <v-avatar color="#DCFCE7" size="52" class="mr-4">
                <v-icon color="#16A34A" size="28">
                  mdi-email-check-outline
                </v-icon>
              </v-avatar>

              <div>
                <h5
                  class="text-subtitle-1 font-weight-bold mb-1"
                  style="color: #166534"
                >
                  Thank You for Your Feedback
                </h5>

                <div class="text-body-2" style="color: #15803d">
                  We appreciate your time and valuable feedback. Together, we
                  can continue improving our products and delivering better
                  experiences.
                </div>
              </div>
            </div>
          </v-card>
        </v-card-text>

        <v-card-actions class="justify-content-center pb-8 px-8">
          <v-btn
            color="#16A34A"
            depressed
            rounded
            large
            elevation="0"
            class="white--text text-capitalize font-weight-bold"
            style="font-size: 14px; color: #ffffff"
            @click="handleSuccessClose"
          >
            <v-icon left>mdi-check</v-icon>
            Done
          </v-btn>
        </v-card-actions>
      </v-card>

      <!-- ERROR -->
      <v-card
        rounded="xl"
        class="overflow-hidden p-2 text-center"
        v-else-if="dialogState.type === 'error'"
      >
        <v-card-text class="p-8 text-center">
          <!-- Error Icon -->
          <v-avatar color="#FEE2E2" size="110" class="mb-5">
            <v-avatar color="#DC2626" size="78">
              <v-icon size="42" color="#ffffff">
                mdi-alert-circle-outline
              </v-icon>
            </v-avatar>
          </v-avatar>

          <!-- Title -->
          <h2 class="text-h5 font-weight-bold red--text text--darken-1 mb-4">
            Oops! Something Went Wrong
          </h2>

          <!-- Status Chip -->
          <v-chip small color="#FEF2F2" text-color="#DC2626" class="mb-5">
            <v-icon left small> mdi-alert-outline </v-icon>
            Submission Failed
          </v-chip>

          <!-- Message -->
          <div
            style="font-size: 14px"
            class="text-body-1 grey--text text--darken-1 mb-5"
          >
            {{ dialogState.message }}
          </div>

          <!-- Details -->
          <v-expansion-panels
            v-if="dialogState.details"
            flat
            class="mb-5 border rounded-lg"
          >
            <v-expansion-panel class="rounded-lg elevation-1">
              <v-expansion-panel-header>
                <div
                  class="d-flex align-items-center justify-space-between w-100"
                >
                  <div class="d-flex align-items-center mr-3">
                    <v-icon color="#DC2626" class="mr-2">
                      mdi-information-outline
                    </v-icon>

                    <span class="font-weight-bold"> Error Details</span>
                  </div>

                  <v-chip x-small color="#FEF2F2" text-color="#DC2626">
                    Error
                  </v-chip>
                </div>
              </v-expansion-panel-header>

              <v-expansion-panel-content>
                <v-card outlined rounded="mf" color="#FEF2F2">
                  <v-card-text
                    style="font-size: 13px"
                    class="text-left text-body-2 grey--text text--darken-3"
                  >
                    {{ dialogState.details }}
                  </v-card-text>
                </v-card>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>

          <!-- Information -->
          <v-alert
            text
            color="#F59E0B"
            icon="mdi-help-circle-outline"
            class="mb-6 text-left"
          >
            <span class="pl-4"
              >Please review the information and try submitting again.</span
            >
          </v-alert>

          <!-- Actions -->
          <div class="d-flex justify-content-center flex-wrap">
            <v-btn
              outlined
              rounded
              large
              color="#64748B"
              class="mr-3 mb-2 px-8 text-capitalize"
              @click="closeDialog"
              style="font-size: 14px"
            >
              <v-icon left> mdi-close </v-icon>

              Cancel
            </v-btn>

            <v-btn
              color="#2563EB"
              rounded
              large
              elevation="0"
              class="white--text mb-2 px-8 text-capitalize"
              style="font-size: 14px; color: #ffffff"
              :loading="submitting"
              @click="retrySubmission"
            >
              <v-icon left> mdi-refresh </v-icon>

              Try Again
            </v-btn>
          </div>
        </v-card-text>
      </v-card>

      <!-- WARNING -->
      <v-card
        rounded="xl"
        class="p-2 text-center"
        v-else-if="dialogState.type === 'warning'"
      >
        <v-card-text class="pt-10 pb-4 px-8">
          <v-avatar color="#FFF7ED" size="90" class="mb-4">
            <v-icon size="48" color="#EA580C"> mdi-alert-outline </v-icon>
          </v-avatar>
          <h2 class="font-weight-bold orange--text text--darken-2 mb-3">
            Notice
          </h2>
          <p
            class="text-body-1 grey--text text--darken-1"
            style="font-size: 14px"
          >
            {{ dialogState.message }}
          </p>
        </v-card-text>
        <v-card-actions class="d-flex justify-content-center pb-8">
          <v-btn
            color="#EA580C"
            depressed
            rounded
            large
            elevation="0"
            style="font-size: 14px; color: #ffffff"
            class="white--text text-capitalize px-8"
            @click="closeDialog"
          >
            <v-icon left>mdi-check</v-icon> Okay
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Token Invalid Dialog -->
    <v-dialog
      v-model="tokenInvalidDialog"
      persistent
      max-width="600"
      :attach="dialogAttach"
    >
      <v-card rounded="xl" elevation="3" class="p-8 text-center">
        <v-card-text class="px-8 py-10">
          <v-avatar color="#FEE2E2" size="100" class="mb-5">
            <v-avatar color="#DC2626" size="72">
              <v-icon size="40" color="#FFFFFF">
                mdi-shield-alert-outline
              </v-icon>
            </v-avatar>
          </v-avatar>

          <h2 class="text-h2 font-weight-bold red--text text--darken-1 mb-3">
            Token Invalid
          </h2>

          <div
            style="font-size: 14px"
            class="text-body-1 grey--text text--darken-1 mb-6"
          >
            The review link is invalid or has been altered. The page will
            refresh to restore the original link.
          </div>

          <v-btn
            color="#DC2626"
            rounded
            large
            elevation="0"
            class="px-8"
            @click="handleTokenInvalidOk"
          >
            <v-icon color="#FFFFFF" left> mdi-refresh </v-icon>

            <span style="color: #ffffff; font-size: 14px"> Refresh </span>
          </v-btn>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapActions } from "vuex";
import ApiService from "@/core/services/api.service";
import { VERIFY_QUOTATION_CUSTOMER } from "@/core/services/store/request.module";

export default {
  name: "CreateReview",

  props: {
    serviceName: {
      type: String,
      default: "Sentron",
    },
  },

  data() {
    return {
      pageLoading: false,
      submitting: false,
      lastSubmittedRating: 0,

      responseDialog: false,
      tokenInvalidDialog: false,
      dialogAttach: null,

      dialogState: {
        type: null,
        message: "",
        details: null,
      },

      survey: {
        overall_satisfaction: null,
        expectations: null,
        service_quality: null,
        value_for_money: null,
        customer_support: null,
        issue_resolved: null,
        loyalty_recomm: null,
        use_again: null,
        liked_most: null,
        improvement: null,
        comment: null,
      },

      token: null,
      originalToken: null,
      tokenValidated: true,
      tokenError: false,
    };
  },

  computed: {
    isFormValid() {
      return (
        this.survey.overall_satisfaction &&
        this.survey.expectations &&
        this.survey.service_quality &&
        this.survey.value_for_money &&
        this.survey.customer_support &&
        this.survey.issue_resolved &&
        this.survey.loyalty_recomm !== null &&
        this.survey.use_again
      );
    },
  },

  methods: {
    ...mapActions({
      verifyQuotationCustomer: VERIFY_QUOTATION_CUSTOMER,
    }),

    handleSuccessClose() {
      window.location.replace("about:blank");
    },

    showDialog(type, message, details = null) {
      this.dialogState = { type, message, details };
      this.responseDialog = true;
    },

    closeDialog() {
      this.responseDialog = false;
      this.dialogState = { type: null, message: "", details: null };
    },

    async retrySubmission() {
      this.closeDialog();
      await this.submitReview();
    },

    refreshPage() {
      window.location.reload();
    },

    handleTokenInvalidOk() {
      this.tokenInvalidDialog = false;
      window.location.reload();
    },

    async validateToken() {
      if (!this.token) {
        this.tokenError = true;
        return false;
      }
      try {
        const tokenParts = this.token.split(".");
        if (tokenParts.length !== 3) throw new Error("Invalid token format");
        const payload = JSON.parse(atob(tokenParts[1]));
        const currentTime = Math.floor(Date.now() / 1000);
        if (payload.exp && payload.exp < currentTime)
          throw new Error("Token has expired");
        const response = await ApiService.getWithCustomToken(
          `reviews/validate-token?token=${this.token}`,
          this.token
        );
        if (response.data && response.data.valid === true) {
          this.tokenValidated = true;
          return true;
        } else {
          throw new Error("Token validation failed on server");
        }
      } catch (error) {
        console.error("Token validation error:", error);
        this.tokenError = true;
        this.tokenValidated = false;
        return false;
      }
    },

    checkTokenIntegrity() {
      const currentToken = this.$route.query.token;
      if (this.originalToken && currentToken !== this.originalToken) {
        this.tokenInvalidDialog = true;
        return false;
      }
      return true;
    },

    async submitReview() {
      if (!this.checkTokenIntegrity()) return;
      if (!this.$refs.reviewForm.validate()) {
        this.showDialog(
          "warning",
          "Please fill in all required fields correctly."
        );
        return;
      }
      if (!this.token) {
        this.showDialog(
          "error",
          "Authentication token is missing. Please refresh the page and try again."
        );
        return;
      }
      this.submitting = true;
      try {
        const reviewPayload = {
          overall_satisfaction: this.survey.overall_satisfaction,
          expectations: this.survey.expectations,
          service_quality: this.survey.service_quality,
          value_for_money: this.survey.value_for_money,
          customer_support: this.survey.customer_support,
          issue_resolved: this.survey.issue_resolved,
          loyalty_recomm: this.survey.loyalty_recomm,
          use_again: this.survey.use_again,
          liked_most: this.survey.liked_most,
          improvement: this.survey.improvement,
          comment: this.survey.comment,
        };
        const response = await this.verifyQuotationCustomer({
          url: "reviews",
          data: reviewPayload,
          token: this.token,
        });
        if (response && response.success === true) {
          this.showDialog(
            "success",
            response.message ||
              "Your response has been submitted successfully! Thank you for your feedback."
          );
          this.resetForm();
          this.$toast?.success("Feedback submitted successfully!");
        } else {
          throw new Error(response?.message || "Failed to submit review");
        }
      } catch (error) {
        let errorMessage = "Failed to submit review. Please try again.";
        let errorDetails = null;
        if (error.response) {
          const { status, data } = error.response;
          switch (status) {
            case 401:
              this.tokenInvalidDialog = true;
              this.submitting = false;
              return;
            case 422:
              errorMessage =
                data.message ||
                "You have already submitted a feedback for this service.";
              if (data.errors)
                errorDetails = JSON.stringify(data.errors, null, 2);
              break;
            case 500:
              errorMessage =
                data.message ||
                "Server error occurred. Please contact support if this continues.";
              if (data.debug)
                errorDetails = JSON.stringify(data.debug, null, 2);
              break;
            case 404:
              errorMessage =
                "Feedback service not found. Please contact support.";
              break;
            default:
              errorMessage =
                data.message || `Error ${status}: ${error.response.statusText}`;
              if (data.debug || data.errors)
                errorDetails = JSON.stringify(
                  data.debug || data.errors,
                  null,
                  2
                );
          }
        } else if (error.request) {
          errorMessage =
            "Network error. Please check your internet connection and try again.";
          errorDetails = "No response received from server";
        } else {
          errorMessage = error.message || "An unexpected error occurred.";
          errorDetails = error.stack || error.toString();
        }
        this.showDialog("error", errorMessage, errorDetails);
        this.$toast?.error(errorMessage);
      } finally {
        this.submitting = false;
      }
    },

    resetForm() {
      this.survey = {
        overall_satisfaction: null,
        expectations: null,
        service_quality: null,
        value_for_money: null,
        customer_support: null,
        issue_resolved: null,
        loyalty_recomm: null,
        use_again: null,
        liked_most: "",
        improvement: "",
        comment: "",
      };
      this.$refs.reviewForm?.resetValidation();
    },

    ensureAppContainer() {
      let appElement = document.querySelector("[data-app]");
      if (!appElement) {
        appElement = document.createElement("div");
        appElement.setAttribute("data-app", "true");
        appElement.style.position = "relative";
        document.body.appendChild(appElement);
      }
      return appElement;
    },
  },

  async mounted() {
    this.token = this.$route.query.token;
    this.originalToken = this.token;
    this.dialogAttach = this.ensureAppContainer();
    if (this.token) {
      this.pageLoading = true;
      const isValid = await this.validateToken();
      this.pageLoading = false;
      if (!isValid) console.log("Token validation failed");
    } else {
      this.tokenError = true;
    }
  },

  watch: {
    $route(to) {
      const newToken = to.query.token;
      if (this.originalToken && newToken !== this.originalToken) {
        this.tokenInvalidDialog = true;
      }
    },
  },
};
</script>

<style scoped>
::v-deep .v-radio {
  margin-bottom: 2px;
}
::v-deep .v-radio .v-label {
  margin-left: 6px;
}
/* ::v-deep textarea {
  resize: none;
} */
::v-deep .v-input__slot {
  border-radius: 8px !important;
}
::-webkit-scrollbar {
  width: 4px;
}
::-webkit-scrollbar-track {
  background: #f1f1f1;
}
::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 2px;
}
::-webkit-scrollbar-thumb:hover {
  background: #9e9e9e;
}

.powered-card {
  border: 1px solid #bfdbfe !important;
  border-radius: 16px !important;
  background: #f8fbff;
  transition: all 0.2s ease;
}

.powered-card:hover {
  border-color: #93c5fd !important;
  box-shadow: 0 4px 12px rgba(37, 99, 235, 0.12);
  transform: translateY(-1px);
}
</style>
